/**
 * Password input with show/hide toggle eye icon.
 *
 * Reused in SignIn.tsx + SignUp.tsx (and the future change-password flow).
 * Toggles between type="password" and type="text" via an inline icon button
 * positioned on the right side of the input. Maintains focus on toggle so
 * users don't lose their place.
 *
 * Per Ean's UX feedback 2026-06-02: standard B2B SaaS pattern, low friction,
 * helps users catch typos before submitting.
 */

import { useState, useRef, type ChangeEvent } from 'react';

// Push 5-pre-smoke Part A3 (2026-07-11) — eye + eye-off glyphs delegated
// to the canonical IconEye + IconEyeOff primitives in Icon.tsx so the
// password-visibility affordance shares vocabulary with the Privacy legal
// tab + any future "eye" surface.
import { IconEye, IconEyeOff } from './Icon.tsx';

interface PasswordInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: 'current-password' | 'new-password';
  required?: boolean;
  minLength?: number;
  disabled?: boolean;
  placeholder?: string;
  // Round 6 Item 19 — opt-in autofocus for password-first forms
  // (ResetPassword, change-password). Default off so SignIn / SignUp
  // can keep focus on the email/name field above.
  autoFocus?: boolean;
}

export function PasswordInput({
  id,
  value,
  onChange,
  autoComplete = 'current-password',
  required = false,
  minLength,
  disabled = false,
  placeholder,
  autoFocus = false,
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  function toggle() {
    setVisible((v) => !v);
    // Restore focus to the input so the user can keep typing after toggling.
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        id={id}
        type={visible ? 'text' : 'password'}
        className="input"
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        disabled={disabled}
        placeholder={placeholder}
        autoFocus={autoFocus}
        style={{ paddingRight: '40px' }}
      />
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
        style={{
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          cursor: disabled ? 'default' : 'pointer',
          padding: '4px',
          color: 'var(--fg-muted)',
          fontSize: 'var(--fs-sm)',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {visible ? <IconEyeOff size={18} /> : <IconEye size={18} />}
      </button>
    </div>
  );
}
